// LearnCore Frontend - 高亮文本组件

import React, { useState, useRef, useEffect } from 'react';
import type { ClassicalKeyWord, ClassicalFunctionWord, ClassicalSpecialChar, ClassicalSentencePattern, ClassicalWordUsage } from '../types';

interface HighlightedWord {
  text: string;
  type: 'key_word' | 'function_word' | 'special_char' | 'sentence_pattern' | 'word_usage';
  id: string;
  startIndex: number;
  endIndex: number;
  charType?: 'tongjiazi' | 'gujinyiyi'; // 用于特殊字符类型
}

interface PopupData {
  word: string;
  type: string;
  content: any;
  position: { x: number; y: number };
}

interface HighlightedTextProps {
  text: string;
  highlights: HighlightedWord[];
  onWordClick: (wordId: string, wordType: string) => void;
  // 新增：用于获取高亮字详细数据的函数
  getWordData?: (wordId: string, wordType: string) => any;
}

// 处理高亮数据的辅助函数
export const processHighlights = (
  keyWords: ClassicalKeyWord[],
  functionWords: ClassicalFunctionWord[],
  specialChars: ClassicalSpecialChar[],
  sentencePatterns: ClassicalSentencePattern[],
  wordUsages: ClassicalWordUsage[],
  originalText: string
): HighlightedWord[] => {
  const highlights: HighlightedWord[] = [];
  let highlightCounter = 0; // 用于生成唯一 key

  // 处理重点实词
  keyWords.forEach((word, wordIndex) => {
    // 对于实词，只使用单个字符匹配，完全忽略 word_occurrences
    // 这样可以避免匹配过长的短语，确保精确匹配
    const searchTerm = word.word; // 只使用单个字符

      // 查找所有匹配位置
      let searchIndex = 0;
      while (searchIndex < originalText.length) {
      const foundIndex = originalText.indexOf(searchTerm, searchIndex);
        if (foundIndex === -1) break;

        highlights.push({
        text: searchTerm,
          type: 'key_word',
        id: `key-word-${word.id}-${wordIndex}-${highlightCounter++}`,
          startIndex: foundIndex,
        endIndex: foundIndex + searchTerm.length
        });

        searchIndex = foundIndex + 1;
      }
  });

  // 处理核心虚词
  functionWords.forEach((word, wordIndex) => {
    // 对于虚词，只使用单个字符匹配，完全忽略 example_occurrences
    // 这样可以避免匹配过长的短语，如 "未复有能与其奇者"
    const searchTerm = word.word; // 只使用单个字符

      // 查找所有匹配位置
      let searchIndex = 0;
      while (searchIndex < originalText.length) {
      const foundIndex = originalText.indexOf(searchTerm, searchIndex);
        if (foundIndex === -1) break;

        highlights.push({
        text: searchTerm,
          type: 'function_word',
        id: `function-word-${word.id}-${wordIndex}-${highlightCounter++}`,
        startIndex: foundIndex,
        endIndex: foundIndex + searchTerm.length
      });

      searchIndex = foundIndex + 1;
    }
  });

  // 处理特殊字符（通假字和古今异义）
  specialChars.forEach((char, charIndex) => {
    // 对于特殊字符，只使用单个字符匹配
    const searchTerm = char.original_char;

    // 查找所有匹配位置
    let searchIndex = 0;
    while (searchIndex < originalText.length) {
      const foundIndex = originalText.indexOf(searchTerm, searchIndex);
      if (foundIndex === -1) break;

      highlights.push({
        text: searchTerm,
        type: 'special_char',
        charType: char.char_type,
        id: `special-char-${char.id}-${charIndex}-${highlightCounter++}`,
        startIndex: foundIndex,
        endIndex: foundIndex + searchTerm.length
      });

      searchIndex = foundIndex + 1;
    }
  });

  // 处理句式
  sentencePatterns.forEach((pattern, patternIndex) => {
    if (pattern.example_occurrences && pattern.example_occurrences.length > 0) {
      pattern.example_occurrences.forEach(occurrence => {
        // 查找所有匹配位置
        let searchIndex = 0;
        while (searchIndex < originalText.length) {
          const foundIndex = originalText.indexOf(occurrence, searchIndex);
          if (foundIndex === -1) break;

          console.log(`Found sentence pattern: "${occurrence}" at position ${foundIndex}`);
          highlights.push({
            text: occurrence,
            type: 'sentence_pattern',
            id: `sentence-pattern-${pattern.id}-${patternIndex}-${highlightCounter++}`,
          startIndex: foundIndex,
            endIndex: foundIndex + occurrence.length
        });

        searchIndex = foundIndex + 1;
      }
    });
    }
  });

  // 处理词类活用
  wordUsages.forEach((usage, usageIndex) => {
    if (usage.word_occurrences && usage.word_occurrences.length > 0) {
      usage.word_occurrences.forEach(occurrence => {
        // 查找所有匹配位置
        let searchIndex = 0;
        while (searchIndex < originalText.length) {
          const foundIndex = originalText.indexOf(occurrence, searchIndex);
          if (foundIndex === -1) break;

          highlights.push({
            text: occurrence,
            type: 'word_usage',
            id: `word-usage-${usage.id}-${usageIndex}-${highlightCounter++}`,
            startIndex: foundIndex,
            endIndex: foundIndex + occurrence.length
          });

          searchIndex = foundIndex + 1;
        }
      });
    }
  });

  // 按位置排序并处理重叠
  const sortedHighlights = highlights.sort((a, b) => a.startIndex - b.startIndex);
  
  // 处理重叠的高亮，支持多重高亮融合
  const filteredHighlights: HighlightedWord[] = [];
  
  for (let i = 0; i < sortedHighlights.length; i++) {
    const current = sortedHighlights[i];
    const prev = filteredHighlights[filteredHighlights.length - 1];
    
    if (!prev || current.startIndex >= prev.endIndex) {
      // 没有重叠，直接添加
      filteredHighlights.push(current);
    } else {
      // 有重叠，检查是否可以融合
      const canMerge = canHighlightsMerge(prev, current);
      
      if (canMerge) {
        // 可以融合，保留两个高亮
        console.log(`Merging highlights: "${prev.text}" and "${current.text}"`);
        filteredHighlights.push(current);
      } else {
        // 不能融合，按优先级处理
        const currentPriority = getHighlightPriority(current);
        const prevPriority = getHighlightPriority(prev);
        
        console.log(`Overlap detected: "${current.text}" (${current.type}, priority: ${currentPriority}) vs "${prev.text}" (${prev.type}, priority: ${prevPriority})`);
        
        if (currentPriority > prevPriority) {
          // 当前优先级更高，替换
          console.log(`Replacing "${prev.text}" with "${current.text}"`);
          filteredHighlights[filteredHighlights.length - 1] = current;
        } else {
          console.log(`Keeping "${prev.text}", discarding "${current.text}"`);
        }
      }
    }
  }
  
  return filteredHighlights;
};

// 判断两个高亮是否可以融合
const canHighlightsMerge = (prev: HighlightedWord, current: HighlightedWord): boolean => {
  // 检查是否有重叠
  const hasOverlap = !(prev.endIndex <= current.startIndex || current.endIndex <= prev.startIndex);
  if (!hasOverlap) {
    return false;
  }
  
  // 相同类型的高亮不能融合
  if (prev.type === current.type) {
    return false;
  }
  
  // 句式可以与任何单个字符高亮融合（有重叠即可）
  if (prev.type === 'sentence_pattern' && (current.type === 'special_char' || current.type === 'function_word' || current.type === 'key_word')) {
    return true;
  }
  if (current.type === 'sentence_pattern' && (prev.type === 'special_char' || prev.type === 'function_word' || prev.type === 'key_word')) {
    return true;
  }
  
  // 词类活用可以与任何单个字符高亮融合（有重叠即可）
  if (prev.type === 'word_usage' && (current.type === 'special_char' || current.type === 'function_word' || current.type === 'key_word')) {
    return true;
  }
  if (current.type === 'word_usage' && (prev.type === 'special_char' || prev.type === 'function_word' || prev.type === 'key_word')) {
    return true;
  }
  
  // 特殊字符可以与实词或虚词融合（有重叠即可）
  if (prev.type === 'special_char' && (current.type === 'function_word' || current.type === 'key_word')) {
    return true;
  }
  if (current.type === 'special_char' && (prev.type === 'function_word' || prev.type === 'key_word')) {
    return true;
  }
  
  // 实词和虚词可以融合（有重叠即可）
  if ((prev.type === 'key_word' && current.type === 'function_word') || 
      (prev.type === 'function_word' && current.type === 'key_word')) {
    return true;
  }
  
  return false;
};

// 获取高亮优先级（数值越大优先级越高）
const getHighlightPriority = (highlight: HighlightedWord): number => {
  if (highlight.type === 'sentence_pattern') return 5; // 句式优先级最高
  if (highlight.type === 'word_usage') return 4; // 词类活用次之
  if (highlight.type === 'special_char') return 3;
  if (highlight.type === 'key_word') return 2;
  if (highlight.type === 'function_word') return 1;
  return 0;
};

const HighlightedText: React.FC<HighlightedTextProps> = React.memo(({
  text,
  highlights,
  onWordClick,
  getWordData
}) => {
  const [popup, setPopup] = useState<PopupData | null>(null);
  const [clickedElement, setClickedElement] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);


  // 点击外部关闭悬浮窗
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popup && !clickedElement?.contains(event.target as Node)) {
        setPopup(null);
        setClickedElement(null);
      }
    };

    if (popup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [popup, clickedElement]);
  // 获取单个高亮的样式类
  const getHighlightStyleClass = (highlight: HighlightedWord): string => {
    if (highlight.type === 'key_word') return 'border-2 border-blue-200 hover:border-blue-300';
    if (highlight.type === 'function_word') return 'border-2 border-green-200 hover:border-green-300';
    if (highlight.type === 'special_char') {
      return highlight.charType === 'tongjiazi' ? 'border-2 border-orange-200 hover:border-orange-300' : 'border-2 border-red-200 hover:border-red-300';
    }
    if (highlight.type === 'sentence_pattern') return 'bg-purple-200 hover:bg-purple-300';
    if (highlight.type === 'word_usage') return 'bg-indigo-200 hover:bg-indigo-300';
    return 'border-2 border-gray-200 hover:border-gray-300';
  };

  // 合并多个样式类
  const getCombinedStyles = (styleClasses: string[]): string => {
    // 去重并合并样式类
    const uniqueClasses = [...new Set(styleClasses)];
    
    // 分离不同类型的样式
    const bgClasses = uniqueClasses.filter(cls => cls.includes('bg-') && !cls.includes('hover:'));
    const borderClasses = uniqueClasses.filter(cls => cls.includes('border-') && !cls.includes('hover:'));
    const hoverBgClasses = uniqueClasses.filter(cls => cls.includes('hover:bg-'));
    const hoverBorderClasses = uniqueClasses.filter(cls => cls.includes('hover:border-'));
    
    // 合并所有样式，保持所有边框样式
    const allClasses = [
      ...bgClasses,
      ...borderClasses,
      ...hoverBgClasses,
      ...hoverBorderClasses
    ];
    
    // 调试输出
    if (styleClasses.length > 1) {
      console.log('Combining styles:', {
        original: styleClasses,
        unique: uniqueClasses,
        bgClasses,
        borderClasses,
        hoverBgClasses,
        hoverBorderClasses,
        result: allClasses.join(' ')
      });
    }
    
    return allClasses.join(' ');
  };

  // 处理多重高亮的点击
  const handleCombinedWordClick = (event: React.MouseEvent, highlights: HighlightedWord[]) => {
    event.preventDefault();
    event.stopPropagation();
    
    // 获取点击元素的位置
    const rect = event.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    
    if (!containerRect) return;
    
    // 计算相对位置
    const x = rect.left - containerRect.left;
    const y = rect.bottom - containerRect.top + 8;
    
    // 显示第一个高亮的详细信息
    const primaryHighlight = highlights[0];
    const wordData = getWordData ? getWordData(primaryHighlight.id, primaryHighlight.type) : null;
    
    setPopup({
      word: primaryHighlight.text,
      type: primaryHighlight.type,
      content: wordData,
      position: { x, y }
    });
    
    setClickedElement(event.currentTarget as HTMLElement);
    
    // 调用第一个高亮的点击处理
    onWordClick(primaryHighlight.id, primaryHighlight.type);
  };

  // 获取多重高亮的标题
  const getCombinedTitle = (highlights: HighlightedWord[]): string => {
    if (highlights.length === 1) {
      const highlight = highlights[0];
      if (highlight.type === 'key_word') return '点击查看实词详解';
      if (highlight.type === 'function_word') return '点击查看虚词详解';
      if (highlight.type === 'special_char') {
        return highlight.charType === 'tongjiazi' ? '点击查看通假字详解' : '点击查看古今异义详解';
      }
      if (highlight.type === 'sentence_pattern') return '点击查看句式详解';
      if (highlight.type === 'word_usage') return '点击查看词类活用详解';
    }
    return '点击查看多重知识点详解';
  };

  // 获取多重高亮的无障碍标签
  const getCombinedAriaLabel = (highlights: HighlightedWord[]): string => {
    if (highlights.length === 1) {
      const highlight = highlights[0];
      if (highlight.type === 'key_word') return `查看实词${highlight.text}的详解`;
      if (highlight.type === 'function_word') return `查看虚词${highlight.text}的详解`;
      if (highlight.type === 'special_char') {
        const typeName = highlight.charType === 'tongjiazi' ? '通假字' : '古今异义';
        return `查看${typeName}${highlight.text}的详解`;
      }
      if (highlight.type === 'sentence_pattern') return `查看句式${highlight.text}的详解`;
      if (highlight.type === 'word_usage') return `查看词类活用${highlight.text}的详解`;
    }
    return `查看${highlights[0].text}的多重知识点详解`;
  };

  const renderHighlightedText = () => {
    if (highlights.length === 0) {
      return <div className="text-gray-800 font-serif leading-relaxed whitespace-pre-wrap">{text}</div>;
    }
    
    // 创建字符级别的样式映射
    const charStyles: { [key: number]: string[] } = {};
    const charHighlights: { [key: number]: HighlightedWord[] } = {};
    
    // 为每个字符位置收集所有高亮样式
    highlights.forEach((highlight) => {
      console.log(`Processing highlight: "${highlight.text}" (${highlight.type}) at [${highlight.startIndex}, ${highlight.endIndex})`);
      for (let i = highlight.startIndex; i < highlight.endIndex; i++) {
        if (!charStyles[i]) {
          charStyles[i] = [];
          charHighlights[i] = [];
        }
        
        const styleClass = getHighlightStyleClass(highlight);
        if (!charStyles[i].includes(styleClass)) {
          charStyles[i].push(styleClass);
        }
        charHighlights[i].push(highlight);
      }
    });
    
    // 调试：输出字符样式映射
    console.log('Character styles mapping:', charStyles);
    console.log('All highlights:', highlights.map(h => ({
      text: h.text,
      type: h.type,
      start: h.startIndex,
      end: h.endIndex
    })));
    
    const elements: React.ReactNode[] = [];
    
    // 为每个字符单独渲染，确保每个字符都能正确显示其样式
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const styles = charStyles[i] || [];
      const highlights = charHighlights[i] || [];
      
      if (styles.length > 0) {
        // 有高亮的字符
        const combinedStyles = getCombinedStyles(styles);
        elements.push(
          <span
            key={`highlight-${i}`}
            className={`cursor-pointer rounded px-1 py-0.5 transition-colors ${combinedStyles}`}
            onClick={(e) => handleCombinedWordClick(e, highlights)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCombinedWordClick(e as any, highlights);
              }
            }}
            title={getCombinedTitle(highlights)}
            role="button"
            tabIndex={0}
            aria-label={getCombinedAriaLabel(highlights)}
          >
            {char}
          </span>
        );
      } else {
        // 普通字符
        elements.push(
          <span key={`text-${i}`}>
            {char}
          </span>
        );
      }
    }
    
    return (
      <div className="text-gray-800 font-serif leading-relaxed whitespace-pre-wrap">
        {elements}
      </div>
    );
  };
  
  // 渲染悬浮窗内容
  const renderPopupContent = () => {
    if (!popup || !popup.content) return null;

    const { word, type, content } = popup;

    // 获取类型对应的颜色主题
    const getTypeTheme = () => {
      switch (type) {
        case 'key_word':
          return {
            border: 'border-blue-200',
            headerText: 'text-blue-800',
            tagBg: 'bg-blue-100',
            tagText: 'text-blue-600',
            accent: 'text-blue-600'
          };
        case 'function_word':
          return {
            border: 'border-green-200',
            headerText: 'text-green-800',
            tagBg: 'bg-green-100',
            tagText: 'text-green-600',
            accent: 'text-green-600'
          };
        case 'special_char':
          const isTongjiazi = content.char_type === 'tongjiazi';
          return {
            border: isTongjiazi ? 'border-orange-200' : 'border-red-200',
            headerText: isTongjiazi ? 'text-orange-800' : 'text-red-800',
            tagBg: isTongjiazi ? 'bg-orange-100' : 'bg-red-100',
            tagText: isTongjiazi ? 'text-orange-600' : 'text-red-600',
            accent: isTongjiazi ? 'text-orange-600' : 'text-red-600'
          };
        case 'sentence_pattern':
          return {
            border: 'border-purple-200',
            headerText: 'text-purple-800',
            tagBg: 'bg-purple-100',
            tagText: 'text-purple-600',
            accent: 'text-purple-600'
          };
        case 'word_usage':
          return {
            border: 'border-indigo-200',
            headerText: 'text-indigo-800',
            tagBg: 'bg-indigo-100',
            tagText: 'text-indigo-600',
            accent: 'text-indigo-600'
          };
        default:
          return {
            border: 'border-gray-200',
            headerText: 'text-gray-800',
            tagBg: 'bg-gray-100',
            tagText: 'text-gray-600',
            accent: 'text-gray-600'
          };
      }
    };

    const theme = getTypeTheme();

    return (
      <div className={`bg-white ${theme.border} border rounded-lg shadow-lg p-4 max-w-sm`}>
        {/* 头部 */}
        <div className="flex items-center justify-between mb-3">
          <h3 className={`${theme.headerText} font-semibold text-lg`}>
            {word}
          </h3>
          <span className={`${theme.tagBg} ${theme.tagText} text-xs font-medium px-2 py-1 rounded`}>
            {type === 'key_word' ? '实词' : 
             type === 'function_word' ? '虚词' :
             type === 'special_char' ? (content.char_type === 'tongjiazi' ? '通假字' : '古今异义') :
             type === 'sentence_pattern' ? '句式' :
             type === 'word_usage' ? '词类活用' : '其他'}
          </span>
        </div>
        
        {/* 内容区域 */}
        <div className="space-y-2 text-sm">
          {type === 'key_word' && (
            <>
              {content.meaning_in_text && (
                <div>
                  <span className={`${theme.accent} font-medium`}>文中含义：</span>
                  <span className="text-gray-700">{content.meaning_in_text}</span>
                </div>
              )}
              {content.multiple_meanings && (
                <div>
                  <span className={`${theme.accent} font-medium`}>多种含义：</span>
                  <span className="text-gray-700">{content.multiple_meanings}</span>
                </div>
              )}
              {content.ancient_modern_diff && (
                <div>
                  <span className={`${theme.accent} font-medium`}>古今差异：</span>
                  <span className="text-gray-700">{content.ancient_modern_diff}</span>
                </div>
              )}
            </>
          )}
          
          {type === 'function_word' && (
            <>
              {content.word_class && (
                <div>
                  <span className={`${theme.accent} font-medium`}>词性：</span>
                  <span className="text-gray-700">{content.word_class}</span>
                </div>
              )}
              {content.function_description && (
                <div>
                  <span className={`${theme.accent} font-medium`}>功能：</span>
                  <span className="text-gray-700">{content.function_description}</span>
                </div>
              )}
            </>
          )}
          
          {type === 'special_char' && (
            <>
              {content.ancient_meaning && (
                <div>
                  <span className={`${theme.accent} font-medium`}>古义：</span>
                  <span className="text-gray-700">{content.ancient_meaning}</span>
                </div>
              )}
              {content.modern_meaning && (
                <div>
                  <span className={`${theme.accent} font-medium`}>今义：</span>
                  <span className="text-gray-700">{content.modern_meaning}</span>
                </div>
              )}
              {content.pronunciation && (
                <div>
                  <span className={`${theme.accent} font-medium`}>读音：</span>
                  <span className="text-gray-700 font-mono">{content.pronunciation}</span>
                </div>
              )}
            </>
          )}
          
          {type === 'sentence_pattern' && (
            <>
              {content.pattern_name && (
                <div>
                  <span className={`${theme.accent} font-medium`}>句式名称：</span>
                  <span className="text-gray-700">{content.pattern_name}</span>
                </div>
              )}
              {content.explanation && (
                <div>
                  <span className={`${theme.accent} font-medium`}>解释：</span>
                  <span className="text-gray-700">{content.explanation}</span>
                </div>
              )}
              {content.normal_order && (
                <div>
                  <span className={`${theme.accent} font-medium`}>正常语序：</span>
                  <span className="text-gray-700">{content.normal_order}</span>
                </div>
              )}
            </>
          )}
          
          {type === 'word_usage' && (
            <>
              {content.usage_type && (
                <div>
                  <span className={`${theme.accent} font-medium`}>活用类型：</span>
                  <span className="text-gray-700">{content.usage_type}</span>
                </div>
              )}
              {content.explanation && (
                <div>
                  <span className={`${theme.accent} font-medium`}>解释：</span>
                  <span className="text-gray-700">{content.explanation}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  console.log('Rendering HighlightedText, popup state:', popup);

  return (
    <div ref={containerRef} className="relative">
      {renderHighlightedText()}
      {popup && (
        <div
          className="absolute z-50"
          style={{
            left: popup.position.x,
            top: popup.position.y,
          }}
        >
          {renderPopupContent()}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.text === nextProps.text &&
    prevProps.highlights.length === nextProps.highlights.length &&
    prevProps.onWordClick === nextProps.onWordClick &&
    prevProps.getWordData === nextProps.getWordData
  );
});

HighlightedText.displayName = 'HighlightedText';

export default HighlightedText;